import { createEmbedChatInstance } from "../embedState"
import type { reopenClosedEmbedChat } from "../embedThunks/reopenClosedEmbedChat"
import type { EmbedChatState } from "../embedTypes"
import { shouldHideEmbedMessage } from "./messages"

type ReopenPending = ReturnType<typeof reopenClosedEmbedChat.pending>
type ReopenFulfilled = ReturnType<typeof reopenClosedEmbedChat.fulfilled>

/**
 * The conversation was deleted under an open tab. Its transcript leaves the view
 * the moment the refusal is known rather than once the resume answers, and
 * scroll-back stops asking for the closed history.
 */
export const clearClosedEmbedChat = (state: EmbedChatState, action: ReopenPending) => {
  const closed = state.chats[action.meta.arg.closedChatId]

  if (!closed) return

  closed.messages = []
  closed.streamingMessages = {}
  closed.hasMoreHistory = false
}

/**
 * Swap the closed chat for the fresh one the resume answered.
 *
 * The store is keyed by chat id and every reader follows `activeChatId`, so moving
 * it IS the adoption — no consumer is handed the new id, and none has to ignore a
 * `chatRoomId` a response carries. The closed entry is deleted outright, so the
 * deleted transcript cannot resurface through any selector.
 */
export const adoptReopenedEmbedChat = (state: EmbedChatState, action: ReopenFulfilled) => {
  const { closedChatId, chatId, isNewChatroom, messages, hasMoreHistory, topic } = action.payload
  const closed = state.chats[closedChatId]

  // A concurrent refusal of the same chat already adopted a fresh one.
  if (!closed) return

  delete state.chats[closedChatId]

  state.chats[chatId] = {
    ...createEmbedChatInstance(chatId, closed.integrationId, closed.sessionId, closed.isPreview),
    deployment: closed.deployment,
    messages: messages.filter((m) => !shouldHideEmbedMessage(m)),
    initializationPhase: "ready",
    isNewChatroom,
    hasMoreHistory,
    topic
  }

  if (state.activeChatId === closedChatId) {
    state.activeChatId = chatId
  }
}
